import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { InputObject } from "../../controls/default";
import { Collaborator, Consultant, TrainingStatus } from "../../repository/data.default";
import { Tag } from "antd";
import { Dayjs } from "dayjs";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createStaticOptionsRequest,
} from "../../hooks/useSearchRequest";
import {
  getCompanies,
  getCollaborators,
  getConsultants,
} from "../../repository/data.repository";
import { nameOf } from "../../utils/typescript";
import { retry } from "../../utils/http";
import { createStatusColorMap } from "../../utils/colors";

export interface TrainingsViewForm {
  readonly company: InputObject;
  readonly host: InputObject;
  readonly status: InputObject<TrainingStatus>;
  readonly title: string;
  readonly startDate: Dayjs[];
  readonly createdOn: Dayjs[];
  readonly createdByUserUserName: string;
}

// Status → tag colour, shared by the grid column and the manage dialog.
export const trainingStatusColor = createStatusColorMap<string>({
  [TrainingStatus.Planned]: "blue",
  [TrainingStatus.Completed]: "green",
  [TrainingStatus.Cancelled]: "default",
});

export const trainingsViewColumns: DataColumn[] = [
  {
    title: "Status",
    key: "status",
    width: 120,
    sort: true,
    defaultSort: "asc",
    transform: (value: string) => (
      <Tag color={trainingStatusColor(value)}>{value}</Tag>
    ),
  },
  {
    title: "Title",
    key: "title",
    width: 220,
    sort: true,
  },
  {
    title: "Company",
    key: "companyName",
    width: 180,
    sort: true,
  },
  {
    title: "Lead trainer",
    key: "hostUserName",
    width: 180,
    sort: true,
  },
  {
    title: "Starts",
    key: "startDate",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Ends",
    key: "endDate",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Completed At",
    key: "completedAt",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
];

const trainingsQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "host", queryField: "hostUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "status", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "title", operation: QueryOperation.Contains },
  { formField: "startDate", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdByUserUserName", operation: QueryOperation.Contains },
];

export const mapTrainingsViewQuery = (values: TrainingsViewForm): SearchQuery =>
  buildSearchQuery(values, trainingsQueryMappings);

// Hosts are affiliates of either flavour — merge the platform consultant and collaborator directories
// into one picker (de-duped by user uid).
const hostsSearchRequest = (search: string) =>
  retry(() =>
    Promise.all([
      getConsultants({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Consultant>("userName"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 50,
      }),
      getCollaborators({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Collaborator>("userName"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 50,
      }),
    ]),
  ).then(([consultants, collaborators]) =>
    Array.from(
      new Map(
        [...consultants.data, ...collaborators.data].map((dataItem) => [
          dataItem.uid,
          {
            display: dataItem.userName,
            subDisplay: dataItem.companyName,
            value: dataItem.uid,
          },
        ]),
      ).values(),
    ),
  );

export const trainingsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "host",
          label: "Lead trainer",
          request: hostsSearchRequest,
        },
        {
          type: "combobox",
          name: "status",
          label: "Status",
          request: createStaticOptionsRequest(
            Object.values(TrainingStatus).map((s) => ({ display: s, value: s })),
          ),
        },
        { type: "textbox", name: "title", label: "Title" },
        { type: "rangetimepicker", name: "startDate", label: "Starts" },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
        { type: "textbox", name: "createdByUserUserName", label: "Created By" },
      ],
    },
  ],
};
